import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { generateClassicDRPCertificate } from '../../utils/classicDrpCertificateGenerator';
import { saveFile } from '../../utils/saveFile';
import {
  FaArrowLeft, FaHospital, FaCalendarAlt, FaUserMd, FaClipboardList,
  FaBook, FaHeartbeat, FaCheckCircle, FaTimesCircle, FaPlus, FaEdit,
  FaTrash, FaDownload, FaSpinner, FaSave, FaTimes, FaInfoCircle,
  FaAward, FaSearch, FaUserPlus, FaUpload, FaHistory, FaExclamationTriangle,
  FaChevronDown, FaChevronUp, FaFilePdf, FaFileImage
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;
const MAX_CERT_SIZE = 8 * 1024 * 1024;
// Incharge assignment is unused in practice — almost every student completes
// a posting via certificate upload only. Hidden, not removed, in case it's
// needed again later.
const SHOW_INCHARGE_INPUT = false;

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toInputDate = (d) => {
  if (!d) return '';
  return new Date(d).toISOString().split('T')[0];
};

const isCompleted = (toDate) => {
  if (!toDate) return false;
  const t = new Date(toDate); t.setHours(0, 0, 0, 0);
  const n = new Date(); n.setHours(0, 0, 0, 0);
  return t < n;
};

const CATEGORIES = [
  'Observed',
  'Assisted',
  'Performed under supervision',
  'Performed Independently'
];

// ─── Reusable add/edit form modal ─────────────────────────────────────────────
const FormModal = ({ title, fields, onSave, onClose, initialValues = {} }) => {
  const [values, setValues] = useState(initialValues);
  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{title}</span>
          <button onClick={onClose} style={styles.iconBtn}><FaTimes /></button>
        </div>
        <div style={{ padding: '16px' }}>
          {fields.map(f => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <label style={styles.label}>{f.label}{f.required && ' *'}</label>
              {f.type === 'select' ? (
                <select value={values[f.key] || ''} onChange={e => set(f.key, e.target.value)} style={styles.input}>
                  <option value="">Select {f.label}</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea rows={4} value={values[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} style={{ ...styles.input, resize: 'vertical' }} />
              ) : (
                <input type={f.type || 'text'} value={values[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} readOnly={f.readOnly} style={{ ...styles.input, ...(f.readOnly ? { background: '#f5f5f5' } : {}) }} />
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button style={styles.primaryBtn} onClick={() => onSave(values)}><FaSave style={{ marginRight: 6 }} /> Save</button>
            <button style={styles.secondaryBtn} onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Entry card ───────────────────────────────────────────────────────────────
const EntryCard = ({ children, onEdit, onDelete }) => (
  <div style={styles.entryCard}>
    <div style={{ flex: 1 }}>{children}</div>
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      {onEdit && <button onClick={onEdit} style={styles.editBtn}><FaEdit size={12} /> Edit</button>}
      {onDelete && <button onClick={onDelete} style={styles.deleteBtn}><FaTrash size={12} /> Delete</button>}
    </div>
  </div>
);

// ─── Section card wrapper ─────────────────────────────────────────────────────
const SectionCard = ({ icon, title, color, children, action }) => (
  <div style={{ ...styles.sectionCard, borderTop: `3px solid ${color}` }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color, fontSize: 20 }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e' }}>{title}</span>
      </div>
      {action}
    </div>
    {children}
  </div>
);

// ─── Certificate upload widget ────────────────────────────────────────────────
const CertUploadWidget = ({ onUpload, uploading, inputId }) => {
  const [file, setFile] = useState(null);
  const [b2Loading, setB2Loading] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_CERT_SIZE) { alert('File must be under 8 MB'); e.target.value = ''; return; }
    if (!['image/jpeg', 'image/png', 'application/pdf'].includes(f.type)) { alert('Only JPG, PNG, or PDF files are allowed'); e.target.value = ''; return; }
    setFile(f);
  };

  const doUpload = async () => {
    if (!file) return;
    setB2Loading(true);
    try {
      const token = localStorage.getItem('token');
      let uploadFile = file;
      if (file.type.startsWith('image/')) {
        try {
          const imageCompression = (await import('browser-image-compression')).default;
          uploadFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
        } catch { /* fallback to original */ }
      }
      const formData = new FormData();
      formData.append('file', uploadFile, file.name);
      const res = await fetch(`${API}/upload/attachment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!data.success) { alert(data.message || 'Upload failed'); return; }
      onUpload({ fileKey: data.attachment.key, fileName: data.attachment.name, mimeType: file.type });
      setFile(null);
    } catch { alert('Upload failed. Please try again.'); }
    finally { setB2Loading(false); }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <input type="file" id={inputId} accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} style={{ display: 'none' }} />
      {!file ? (
        <label htmlFor={inputId} style={{ ...styles.outlineBtn, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '8px 16px' }}>
          <FaUpload style={{ marginRight: 6 }} /> Choose Certificate (JPG/PNG/PDF, max 8 MB)
        </label>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 8 }}>
          {file.type === 'application/pdf' ? <FaFilePdf style={{ color: '#dc2626', fontSize: 20 }} /> : <FaFileImage style={{ color: '#2563eb', fontSize: 20 }} />}
          <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
          <span style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>{(file.size / 1024).toFixed(0)} KB</span>
          <button onClick={doUpload} disabled={uploading || b2Loading} style={styles.primaryBtn}>
            {(uploading || b2Loading) ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaUpload style={{ marginRight: 4 }} />Upload</>}
          </button>
          <button onClick={() => setFile(null)} style={styles.iconBtn}><FaTimes /></button>
        </div>
      )}
    </div>
  );
};

// ─── Past DRP card ────────────────────────────────────────────────────────────
const PastDRPCard = ({ hist, index, onUploadCert, onDownloadUploadedCert, onDownloadSystemCert, uploading, downloading, onDelete, deleting }) => {
  const [expanded, setExpanded] = useState(false);
  const v = hist.inchargeVerification || {};
  const hasCert = hist.certificate?.issued || hist.uploadedCertificate?.fileName;
  const noIncharge = !hist.profile?.inchargeId && !hist.profile?.inchargeName;
  const needsUpload = !hasCert;

  return (
    <div style={{ ...styles.pastDRPCard, border: needsUpload ? '1px solid #fca5a5' : '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ ...styles.drpIndexBadge, background: needsUpload ? '#fee2e2' : '#d1fae5', color: needsUpload ? '#b91c1c' : '#065f46' }}>
          DRP {index}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{hist.profile?.hospitalName || '—'}</div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                <FaCalendarAlt size={10} style={{ marginRight: 4 }} />
                {formatDate(hist.profile?.fromDate)} – {formatDate(hist.profile?.toDate)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                <FaUserMd size={10} style={{ marginRight: 4 }} />
                {hist.profile?.inchargeName || (hist.profile?.inchargeId ? 'Incharge assigned' : <em>No Incharge</em>)}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              {hist.certificate?.issued && (
                <span style={styles.badgeSystemCert}><FaAward size={9} style={{ marginRight: 3 }} />System Cert</span>
              )}
              {hist.uploadedCertificate?.fileName && (
                <span style={styles.badgeUploadedCert}><FaUpload size={9} style={{ marginRight: 3 }} />Uploaded Cert</span>
              )}
              {needsUpload && (
                <span style={styles.badgeNoCert}><FaExclamationTriangle size={9} style={{ marginRight: 3 }} />No Certificate</span>
              )}
            </div>
          </div>

          {/* Verification mini badges */}
          <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { key: 'attendanceVerified', label: 'Att' },
              { key: 'diaryVerified', label: 'Diary' },
              { key: 'operationsVerified', label: 'Ops' },
              { key: 'publicHealthVerified', label: 'PH' }
            ].map(item => (
              <span key={item.key} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 8, background: v[item.key] ? '#d1fae5' : '#f3f4f6', color: v[item.key] ? '#065f46' : '#999' }}>
                {v[item.key] ? '✓' : '○'} {item.label}
              </span>
            ))}
          </div>

          {/* No incharge warning */}
          {noIncharge && needsUpload && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
              <FaExclamationTriangle style={{ marginRight: 6 }} />
              No DRP Incharge was assigned. Upload your certificate manually to mark this DRP complete.
            </div>
          )}

          {/* Certificate actions */}
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {hist.certificate?.issued && (
              <button onClick={() => onDownloadSystemCert(hist)} disabled={downloading === hist._id} style={styles.certActionBtn}>
                {downloading === hist._id ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload style={{ marginRight: 4 }} />}
                Download System Certificate
              </button>
            )}
            {hist.uploadedCertificate?.fileName && (
              <button onClick={() => onDownloadUploadedCert(hist._id)} disabled={downloading === hist._id} style={{ ...styles.certActionBtn, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                {downloading === hist._id ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaDownload style={{ marginRight: 4 }} />}
                Download Uploaded Certificate
              </button>
            )}
            <button onClick={() => setExpanded(x => !x)} style={{ ...styles.iconBtn, fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
              {expanded ? <FaChevronUp /> : <FaChevronDown />} {expanded ? 'Hide Details' : 'View Details'}
            </button>
            <button onClick={() => onDelete(hist._id)} disabled={deleting === hist._id} style={{ ...styles.iconBtn, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
              {deleting === hist._id ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaTrash size={12} />} Delete
            </button>
          </div>

          {/* Upload section */}
          {needsUpload && (
            <CertUploadWidget
              inputId={`cert-hist-${hist._id}`}
              uploading={uploading === hist._id}
              onUpload={(payload) => onUploadCert(hist._id, payload)}
            />
          )}

          {/* Expanded details */}
          {expanded && (
            <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginBottom: 8 }}>
                <div><span style={{ color: '#888' }}>Attendance Days: </span><strong>{hist.attendance?.length || 0}</strong></div>
                <div><span style={{ color: '#888' }}>Diary Entries: </span><strong>{hist.diary?.length || 0}</strong></div>
                <div><span style={{ color: '#888' }}>Operations: </span><strong>{hist.operations?.length || 0}</strong></div>
                <div><span style={{ color: '#888' }}>Public Health: </span><strong>{hist.publicHealth?.length || 0}</strong></div>
              </div>
              {v.remarks && (
                <div style={{ padding: '8px 10px', background: '#f8f8f8', borderRadius: 8, borderLeft: '3px solid #0f766e', fontSize: 12 }}>
                  <strong>Incharge Remarks:</strong> {v.remarks}
                </div>
              )}
              {hist.certificate?.issuedAt && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#059669' }}>Certificate issued: {formatDate(hist.certificate.issuedAt)}</div>
              )}
              {hist.uploadedCertificate?.uploadedAt && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#2563eb' }}>Certificate uploaded: {formatDate(hist.uploadedCertificate.uploadedAt)} ({hist.uploadedCertificate.fileName})</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ClassicDRPModule = ({ onNavigate }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseNames, setCourseNames] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [inchargeResults, setInchargeResults] = useState([]);
  const [inchargeSearch, setInchargeSearch] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [showCreateIncharge, setShowCreateIncharge] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [setupForm, setSetupForm] = useState({ hospitalName: '', fromDate: '', toDate: '', inchargeId: '', inchargeName: '' });
  const [newInchargeForm, setNewInchargeForm] = useState({ firstName: '', lastName: '', email: '', password: '', hospitalName: '' });
  const [creatingIncharge, setCreatingIncharge] = useState(false);
  const [toast, setToast] = useState('');
  const [certUploading, setCertUploading] = useState(null); // null | 'current' | histId
  const [certDownloading, setCertDownloading] = useState(null); // null | 'current' | histId
  const [startingNewDRP, setStartingNewDRP] = useState(false);
  const [deletingDRP, setDeletingDRP] = useState(null); // null | histId
  const [deletingCurrentDRP, setDeletingCurrentDRP] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const loadEntry = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/classic-drp/my-entry`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        setEntry(d.entry);
        if (!d.entry.profile?.hospitalName) setShowSetup(true);
      }
    } catch { setError('Failed to load DRP data'); }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadEntry();
    fetch(`${API}/classic-drp/course-names`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setCourseNames(d.courses); }).catch(() => {});
    fetch(`${API}/classic-drp/incharge/hospitals`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setHospitals(d.hospitals); }).catch(() => {});
  }, [loadEntry, token]);

  useEffect(() => {
    if (!inchargeSearch || inchargeSearch.length < 2) { setInchargeResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/classic-drp/incharge/search?q=${encodeURIComponent(inchargeSearch)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { if (d.success) setInchargeResults(d.results); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [inchargeSearch, token]);

  useEffect(() => {
    if (entry?.profile) {
      const p = entry.profile;
      const inchargeData = p.inchargeId;
      setSetupForm({
        hospitalName: p.hospitalName || '',
        fromDate: toInputDate(p.fromDate),
        toDate: toInputDate(p.toDate),
        inchargeId: inchargeData?._id || '',
        inchargeName: inchargeData ? `${inchargeData.profile?.firstName || ''} ${inchargeData.profile?.lastName || ''}`.trim() : ''
      });
    }
  }, [entry]);

  const saveSetup = async () => {
    if (!setupForm.hospitalName || !setupForm.fromDate || !setupForm.toDate) {
      showToast('Please fill Hospital Name, From Date and To Date'); return;
    }
    setSavingSetup(true);
    try {
      const r = await fetch(`${API}/classic-drp/setup`, {
        method: 'POST', headers,
        body: JSON.stringify({ hospitalName: setupForm.hospitalName, fromDate: setupForm.fromDate, toDate: setupForm.toDate, inchargeId: setupForm.inchargeId || undefined })
      });
      const d = await r.json();
      if (d.success) { setEntry(d.entry); setShowSetup(false); showToast('Posting details saved'); }
      else showToast(d.message || 'Failed to save');
    } catch { showToast('Failed to save setup'); }
    setSavingSetup(false);
  };

  const createIncharge = async () => {
    const { firstName, lastName, email, password, hospitalName } = newInchargeForm;
    if (!firstName || !lastName || !email || !password || !hospitalName) { showToast('All fields are required'); return; }
    setCreatingIncharge(true);
    try {
      const r = await fetch(`${API}/classic-drp/incharge/create`, { method: 'POST', headers, body: JSON.stringify({ firstName, lastName, email, password, hospitalName }) });
      const d = await r.json();
      if (d.success) {
        showToast('Incharge account created! You can now select them.');
        setSetupForm(prev => ({ ...prev, inchargeId: d.incharge._id, inchargeName: d.incharge.name, hospitalName: d.incharge.hospitalName }));
        setHospitals(prev => [...new Set([...prev, d.incharge.hospitalName])]);
        setShowCreateIncharge(false);
        setNewInchargeForm({ firstName: '', lastName: '', email: '', password: '', hospitalName: '' });
      } else showToast(d.message || 'Failed to create incharge');
    } catch { showToast('Failed to create incharge'); }
    setCreatingIncharge(false);
  };

  const apiAction = async (method, path, body) => {
    const r = await fetch(`${API}/classic-drp${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
    return r.json();
  };

  const handleAdd = async (type, values) => {
    let path, body;
    if (type === 'attendance') { path = '/attendance'; body = { date: values.date, posting: values.posting }; }
    else if (type === 'diary') { path = '/diary'; body = { date: values.date, entry: values.entry }; }
    else if (type === 'operation') { path = '/operation'; body = { date: values.date, patientName: values.patientName, ipNumber: values.ipNumber, category: values.category }; }
    else if (type === 'publicHealth') { path = '/public-health'; body = { date: values.date, activity: values.activity }; }
    const d = await apiAction('POST', path, body);
    if (d.success) { await loadEntry(); showToast('Record added'); setActiveModal(null); }
    else showToast(d.message || 'Failed to add');
  };

  const handleEdit = async (type, id, values) => {
    let path, body;
    if (type === 'attendance') { path = `/attendance/${id}`; body = { date: values.date, posting: values.posting }; }
    else if (type === 'diary') { path = `/diary/${id}`; body = { date: values.date, entry: values.entry }; }
    else if (type === 'operation') { path = `/operation/${id}`; body = { date: values.date, patientName: values.patientName, ipNumber: values.ipNumber, category: values.category }; }
    else if (type === 'publicHealth') { path = `/public-health/${id}`; body = { date: values.date, activity: values.activity }; }
    const d = await apiAction('PUT', path, body);
    if (d.success) { await loadEntry(); showToast('Record updated'); setActiveModal(null); }
    else showToast(d.message || 'Failed to update');
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this record?')) return;
    const path = type === 'attendance' ? `/attendance/${id}` : type === 'diary' ? `/diary/${id}` : type === 'operation' ? `/operation/${id}` : `/public-health/${id}`;
    const d = await apiAction('DELETE', path);
    if (d.success) { await loadEntry(); showToast('Record deleted'); }
    else showToast(d.message || 'Failed to delete');
  };

  const downloadSystemCert = async (profileData) => {
    setDownloading(true);
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const studentName = `${me.profile?.firstName || ''} ${me.profile?.lastName || ''}`.trim();
    const courseName = me.assignedCourses?.[0]?.courseName || '';
    const inchargeName = profileData?.inchargeName ||
      (profileData?.inchargeId ? `${profileData.inchargeId.profile?.firstName || ''} ${profileData.inchargeId.profile?.lastName || ''}`.trim() : '');
    await generateClassicDRPCertificate({ studentName, courseName, hospitalName: profileData?.hospitalName, fromDate: profileData?.fromDate, toDate: profileData?.toDate, inchargeName }, collegeConfig.collegeName, collegeConfig.logo);
    setDownloading(false);
  };

  const downloadUploadedCert = async (histId) => {
    const path = histId === 'current' ? '/certificate' : `/history/${histId}/certificate`;
    setCertDownloading(histId);
    try {
      const r = await fetch(`${API}/classic-drp${path}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) {
        await saveFile(d.data, d.mimeType, d.fileName || 'certificate');
      } else showToast('Download failed');
    } catch { showToast('Download failed'); }
    setCertDownloading(null);
  };

  const uploadCertificate = async (histId, payload) => {
    const path = histId === 'current' ? '/upload-certificate' : `/history/${histId}/upload-certificate`;
    setCertUploading(histId);
    try {
      const r = await fetch(`${API}/classic-drp${path}`, { method: 'POST', headers, body: JSON.stringify(payload) });
      const d = await r.json();
      if (d.success) { showToast('Certificate uploaded successfully'); await loadEntry(); }
      else showToast(d.message || 'Upload failed');
    } catch { showToast('Upload failed'); }
    setCertUploading(null);
  };

  const startNewDRP = async () => {
    if (!window.confirm('Archive this DRP and start a new one? Your current records will be saved in history.')) return;
    setStartingNewDRP(true);
    try {
      const r = await fetch(`${API}/classic-drp/new-drp`, { method: 'POST', headers });
      const d = await r.json();
      if (d.success) { setEntry(d.entry); setShowSetup(true); showToast('New DRP started. Set up your new posting details.'); }
      else showToast(d.message || 'Failed to start new DRP');
    } catch { showToast('Failed to start new DRP'); }
    setStartingNewDRP(false);
  };

  const deleteCurrentDRP = async () => {
    if (!window.confirm('Permanently delete this DRP? All attendance, diary, operations, public health records and any uploaded certificate will be removed. This cannot be undone.')) return;
    setDeletingCurrentDRP(true);
    try {
      const r = await fetch(`${API}/classic-drp/current`, { method: 'DELETE', headers });
      const d = await r.json();
      if (d.success) { setEntry(d.entry); setShowSetup(true); showToast('Current DRP deleted'); }
      else showToast(d.message || 'Failed to delete current DRP');
    } catch { showToast('Failed to delete current DRP'); }
    setDeletingCurrentDRP(false);
  };

  const deleteDRP = async (histId) => {
    if (!window.confirm('Permanently delete this past DRP entry? This cannot be undone, and any uploaded certificate will also be removed.')) return;
    setDeletingDRP(histId);
    try {
      const r = await fetch(`${API}/classic-drp/history/${histId}`, { method: 'DELETE', headers });
      const d = await r.json();
      if (d.success) { setEntry(d.entry); showToast('DRP entry deleted'); }
      else showToast(d.message || 'Failed to delete DRP entry');
    } catch { showToast('Failed to delete DRP entry'); }
    setDeletingDRP(null);
  };

  // ── Modal field configs ───────────────────────────────────────────────────
  const attendanceFields = [
    { key: 'date', label: 'Date', type: 'date', required: true },
    { key: 'posting', label: 'Posting (Department)', type: 'text', placeholder: 'e.g. General Medicine', required: true }
  ];
  const diaryFields = [
    { key: 'date', label: 'Date', type: 'date', required: true },
    { key: 'day', label: 'Day (auto-filled)', type: 'text', readOnly: true },
    { key: 'entry', label: 'Diary Entry', type: 'textarea', placeholder: 'Write your diary entry here...', required: true }
  ];
  const operationFields = [
    { key: 'date', label: 'Date', type: 'date', required: true },
    { key: 'patientName', label: 'Name of Patient', type: 'text', placeholder: 'Patient full name', required: true },
    { key: 'ipNumber', label: 'IP Number', type: 'text', placeholder: 'IP/Registration number' },
    { key: 'category', label: 'Category', type: 'select', options: CATEGORIES, required: true }
  ];
  const publicHealthFields = [
    { key: 'date', label: 'Date', type: 'date', required: true },
    { key: 'activity', label: 'Activity', type: 'text', placeholder: 'e.g. Health camp, NHP programme', required: true }
  ];

  if (loading) return (
    <div style={styles.centered}>
      <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 32, color: '#b11217' }} />
    </div>
  );

  const v = entry?.inchargeVerification || {};
  const allVerified = v.attendanceVerified && v.diaryVerified && v.operationsVerified && v.publicHealthVerified;
  const currentCompleted = isCompleted(entry?.profile?.toDate);
  const hasIncharge = !!(entry?.profile?.inchargeId);
  const currentHasCert = !!(entry?.certificate?.issued || entry?.uploadedCertificate?.fileName);
  const noInchargeAndCompleted = currentCompleted && !hasIncharge && !currentHasCert;
  const drpHistory = entry?.drpHistory || [];

  return (
    <div style={styles.container}>
      {toast && <div style={styles.toast}>{toast}</div>}

      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => onNavigate('dashboard')} style={styles.backBtn}><FaArrowLeft /></button>
        <div>
          <h1 style={styles.headerTitle}>District Residency Programme</h1>
          <span style={styles.headerSub}>Classic DRP</span>
        </div>
        {drpHistory.length > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888', background: '#f3f4f6', padding: '4px 10px', borderRadius: 12 }}>
            {drpHistory.length + 1} DRPs total
          </span>
        )}
      </div>

      {/* Instructions Banner */}
      <div style={styles.infoBanner}>
        <FaInfoCircle style={{ color: '#2563eb', flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong style={{ display: 'block', marginBottom: 4 }}>How Classic DRP Works:</strong>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8, fontSize: 13 }}>
            <li>Set up your posting details — enter hospital name and from/to dates.</li>
            <li>Record your daily attendance, diary entries, operation records, and public health activities below (optional).</li>
            <li>Once your posting ends, upload your DRP certificate to complete it.</li>
          </ol>
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Completed DRP banner */}
      {currentCompleted && entry?.profile?.hospitalName && (
        <div style={styles.completedBanner}>
          <FaCheckCircle style={{ color: '#059669', marginRight: 8, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <strong>This DRP posting is complete</strong> (ended {formatDate(entry.profile.toDate)})
            {!currentHasCert && <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 13 }}>— Certificate pending</span>}
          </div>
          <button onClick={startNewDRP} disabled={startingNewDRP} style={{ ...styles.primaryBtn, fontSize: 13, padding: '8px 14px', flexShrink: 0 }}>
            {startingNewDRP ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaPlus style={{ marginRight: 4 }} />Start New DRP</>}
          </button>
        </div>
      )}

      {/* Setup Card */}
      <SectionCard icon={<FaHospital />} title="Posting Details" color="#b11217"
        action={!showSetup && entry?.profile?.hospitalName && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowSetup(true)} style={styles.outlineBtn}><FaEdit size={12} style={{ marginRight: 4 }} /> Edit</button>
            <button onClick={deleteCurrentDRP} disabled={deletingCurrentDRP} style={{ ...styles.outlineBtn, color: '#dc2626', borderColor: '#dc2626' }}>
              {deletingCurrentDRP ? <FaSpinner size={12} style={{ animation: 'spin 1s linear infinite', marginRight: 4 }} /> : <FaTrash size={12} style={{ marginRight: 4 }} />} Delete
            </button>
          </div>
        )}
      >
        {!showSetup && entry?.profile?.hospitalName ? (
          <div style={styles.detailGrid}>
            <div style={styles.detailItem}><span style={styles.detailLabel}>Hospital</span><span>{entry.profile.hospitalName}</span></div>
            <div style={styles.detailItem}><span style={styles.detailLabel}>From</span><span>{formatDate(entry.profile.fromDate)}</span></div>
            <div style={styles.detailItem}><span style={styles.detailLabel}>To</span><span>{formatDate(entry.profile.toDate)}</span></div>
            {(SHOW_INCHARGE_INPUT || entry.profile.inchargeId) && (
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>DRP Incharge</span>
                <span>{entry.profile.inchargeId ? `${entry.profile.inchargeId.profile?.firstName || ''} ${entry.profile.inchargeId.profile?.lastName || ''}`.trim() : <em style={{ color: '#999' }}>Not selected</em>}</span>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 14 }}>
              <label style={styles.label}>Hospital Name *</label>
              <input type="text" list="hospital-suggestions" value={setupForm.hospitalName} onChange={e => setSetupForm(p => ({ ...p, hospitalName: e.target.value }))} placeholder="Type hospital name..." style={styles.input} />
              <datalist id="hospital-suggestions">{hospitals.map(h => <option key={h} value={h} />)}</datalist>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>From Date *</label>
                <input type="date" value={setupForm.fromDate} onChange={e => setSetupForm(p => ({ ...p, fromDate: e.target.value }))} style={styles.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>To Date *</label>
                <input type="date" value={setupForm.toDate} onChange={e => setSetupForm(p => ({ ...p, toDate: e.target.value }))} style={styles.input} />
              </div>
            </div>

            {SHOW_INCHARGE_INPUT && (
              <>
                <label style={styles.label}>DRP Incharge</label>
                {setupForm.inchargeId ? (
                  <div style={styles.selectedIncharge}>
                    <FaUserMd style={{ color: '#b11217', marginRight: 8 }} />
                    <span style={{ flex: 1 }}>{setupForm.inchargeName}</span>
                    <button onClick={() => setSetupForm(p => ({ ...p, inchargeId: '', inchargeName: '' }))} style={styles.iconBtn}><FaTimes /></button>
                  </div>
                ) : (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ position: 'relative' }}>
                      <FaSearch style={{ position: 'absolute', left: 10, top: 11, color: '#999' }} />
                      <input type="text" value={inchargeSearch} onChange={e => setInchargeSearch(e.target.value)} placeholder="Search incharge by name or email..." style={{ ...styles.input, paddingLeft: 32 }} />
                    </div>
                    {inchargeResults.length > 0 && (
                      <div style={styles.searchDropdown}>
                        {inchargeResults.map(r => (
                          <div key={r._id} style={styles.searchItem} onClick={() => { setSetupForm(p => ({ ...p, inchargeId: r._id, inchargeName: r.name, hospitalName: r.hospitalName || p.hospitalName })); setInchargeSearch(''); setInchargeResults([]); }}>
                            <FaUserMd style={{ color: '#b11217', marginRight: 8 }} />
                            <div>
                              <div style={{ fontWeight: 600 }}>{r.name}</div>
                              <div style={{ fontSize: 12, color: '#666' }}>{r.email} · {r.hospitalName}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <button onClick={() => setShowCreateIncharge(!showCreateIncharge)} style={styles.linkBtn}>
                  <FaUserPlus size={12} style={{ marginRight: 6 }} />
                  {showCreateIncharge ? 'Hide' : 'Create New DRP Incharge Account'}
                </button>

                {showCreateIncharge && (
                  <div style={styles.createInchargeBox}>
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>Create a login account for your DRP Incharge. They will use the email and password to login.</p>
                    {[
                      { key: 'firstName', label: 'First Name', placeholder: 'First name' },
                      { key: 'lastName', label: 'Last Name', placeholder: 'Last name' },
                      { key: 'email', label: 'Email', placeholder: 'Incharge email address' },
                      { key: 'password', label: 'Password', placeholder: 'Create a password', type: 'password' },
                      { key: 'hospitalName', label: 'Hospital Name', placeholder: 'Hospital where incharge works' }
                    ].map(f => (
                      <div key={f.key} style={{ marginBottom: 10 }}>
                        <label style={styles.label}>{f.label} *</label>
                        <input type={f.type || 'text'} value={newInchargeForm[f.key]} onChange={e => setNewInchargeForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} style={styles.input} />
                      </div>
                    ))}
                    <button onClick={createIncharge} disabled={creatingIncharge} style={styles.primaryBtn}>
                      {creatingIncharge ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaUserPlus style={{ marginRight: 6 }} />Create Incharge Account</>}
                    </button>
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={saveSetup} disabled={savingSetup} style={styles.primaryBtn}>
                {savingSetup ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaSave style={{ marginRight: 6 }} />Save Posting Details</>}
              </button>
              {entry?.profile?.hospitalName && <button onClick={() => setShowSetup(false)} style={styles.secondaryBtn}>Cancel</button>}
            </div>
          </div>
        )}
      </SectionCard>

      {/* Daily Attendance */}
      <SectionCard icon={<FaCalendarAlt />} title="Daily Attendance" color="#16a085"
        action={<button onClick={() => setActiveModal({ type: 'attendance' })} style={styles.addBtn}><FaPlus size={10} style={{ marginRight: 4 }} /> Add</button>}
      >
        {entry?.attendance?.length === 0 ? (
          <p style={styles.emptyText}>No attendance records yet. Click Add to record.</p>
        ) : (
          entry?.attendance?.map(a => (
            <EntryCard key={a._id} onEdit={() => setActiveModal({ type: 'attendance', id: a._id, data: { date: toInputDate(a.date), posting: a.posting } })} onDelete={() => handleDelete('attendance', a._id)}>
              <div style={styles.entryRow}>
                <span style={styles.entryDate}><FaCalendarAlt size={11} style={{ marginRight: 4 }} />{formatDate(a.date)}</span>
                <span style={styles.entryTag}>{a.posting}</span>
              </div>
            </EntryCard>
          ))
        )}
        <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Total: {entry?.attendance?.length || 0} day(s)</p>
      </SectionCard>

      {/* Daily Diary */}
      <SectionCard icon={<FaBook />} title="Record of Daily Diary" color="#2563eb"
        action={<button onClick={() => setActiveModal({ type: 'diary' })} style={styles.addBtn}><FaPlus size={10} style={{ marginRight: 4 }} /> Add</button>}
      >
        {entry?.diary?.length === 0 ? (
          <p style={styles.emptyText}>No diary entries yet. Click Add to record.</p>
        ) : (
          entry?.diary?.map(d => (
            <EntryCard key={d._id} onEdit={() => setActiveModal({ type: 'diary', id: d._id, data: { date: toInputDate(d.date), day: d.day || '', entry: d.entry } })} onDelete={() => handleDelete('diary', d._id)}>
              <div style={styles.entryRow}>
                <span style={styles.entryDate}><FaCalendarAlt size={11} style={{ marginRight: 4 }} />{formatDate(d.date)}</span>
                <span style={{ ...styles.entryTag, background: '#dbeafe', color: '#1d4ed8' }}>{d.day || ''}</span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#333', lineHeight: 1.5 }}>{d.entry}</p>
            </EntryCard>
          ))
        )}
      </SectionCard>

      {/* Record of Operations */}
      <SectionCard icon={<FaClipboardList />} title="Record of Operations" color="#7c3aed"
        action={<button onClick={() => setActiveModal({ type: 'operation' })} style={styles.addBtn}><FaPlus size={10} style={{ marginRight: 4 }} /> Add</button>}
      >
        {entry?.operations?.length === 0 ? (
          <p style={styles.emptyText}>No operation records yet. Click Add to record.</p>
        ) : (
          entry?.operations?.map(op => (
            <EntryCard key={op._id} onEdit={() => setActiveModal({ type: 'operation', id: op._id, data: { date: toInputDate(op.date), patientName: op.patientName, ipNumber: op.ipNumber || '', category: op.category } })} onDelete={() => handleDelete('operation', op._id)}>
              <div style={styles.entryRow}>
                <span style={styles.entryDate}><FaCalendarAlt size={11} style={{ marginRight: 4 }} />{formatDate(op.date)}</span>
                <span style={{ ...styles.entryTag, background: '#ede9fe', color: '#6d28d9' }}>{op.category}</span>
              </div>
              <div style={{ fontSize: 13, color: '#333', marginTop: 4 }}>
                <strong>Patient:</strong> {op.patientName} {op.ipNumber && <span style={{ color: '#666' }}>· IP: {op.ipNumber}</span>}
              </div>
            </EntryCard>
          ))
        )}
        <p style={{ fontSize: 12, color: '#888', marginTop: 8 }}>Total: {entry?.operations?.length || 0} operation(s)</p>
      </SectionCard>

      {/* Public Health */}
      <SectionCard icon={<FaHeartbeat />} title="Public Health / National Health Programme Activities" color="#dc6803"
        action={<button onClick={() => setActiveModal({ type: 'publicHealth' })} style={styles.addBtn}><FaPlus size={10} style={{ marginRight: 4 }} /> Add</button>}
      >
        {entry?.publicHealth?.length === 0 ? (
          <p style={styles.emptyText}>No public health activities yet. Click Add to record.</p>
        ) : (
          entry?.publicHealth?.map(ph => (
            <EntryCard key={ph._id} onEdit={() => setActiveModal({ type: 'publicHealth', id: ph._id, data: { date: toInputDate(ph.date), activity: ph.activity } })} onDelete={() => handleDelete('publicHealth', ph._id)}>
              <div style={styles.entryRow}>
                <span style={styles.entryDate}><FaCalendarAlt size={11} style={{ marginRight: 4 }} />{formatDate(ph.date)}</span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#333' }}>{ph.activity}</p>
            </EntryCard>
          ))
        )}
      </SectionCard>

      {/* Incharge Verification Status */}
      <SectionCard icon={<FaUserMd />} title="Incharge Verification Status" color="#0f766e">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { key: 'attendanceVerified', label: 'Attendance' },
            { key: 'diaryVerified', label: 'Daily Diary' },
            { key: 'operationsVerified', label: 'Operations' },
            { key: 'publicHealthVerified', label: 'Public Health' }
          ].map(item => (
            <div key={item.key} style={{ ...styles.verifyBadge, background: v[item.key] ? '#d1fae5' : '#fee2e2' }}>
              {v[item.key] ? <FaCheckCircle style={{ color: '#059669', marginRight: 6 }} /> : <FaTimesCircle style={{ color: '#dc2626', marginRight: 6 }} />}
              <span style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</span>
            </div>
          ))}
        </div>
        {v.remarks && <p style={{ marginTop: 10, fontSize: 13, color: '#555', padding: '10px', background: '#f8f8f8', borderRadius: 8, borderLeft: '3px solid #0f766e' }}><strong>Incharge Remarks:</strong> {v.remarks}</p>}
        {!allVerified && <p style={{ marginTop: 10, fontSize: 12, color: '#888' }}>All sections must be verified by your DRP Incharge before the certificate can be issued.</p>}
      </SectionCard>

      {/* ─── Certificates & DRP History ───────────────────────────────────── */}
      <SectionCard
        icon={<FaAward />}
        title={drpHistory.length > 0 ? `Certificates & DRP History` : 'Certificate'}
        color="#b45309"
        action={drpHistory.length > 0 && (
          <span style={{ background: '#ede9fe', color: '#7c3aed', fontSize: 11, padding: '3px 10px', borderRadius: 12, fontWeight: 700 }}>
            {drpHistory.length + 1} DRPs
          </span>
        )}
      >
        {/* ── Current DRP certificate ── */}
        <div style={{ marginBottom: drpHistory.length > 0 ? 16 : 0 }}>
          {drpHistory.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current DRP</span>
              {entry?.profile?.hospitalName && (
                <span style={{ fontSize: 12, color: '#555' }}>· {entry.profile.hospitalName}</span>
              )}
            </div>
          )}

          {entry?.certificate?.issued ? (
            <div style={styles.certRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                <FaAward style={{ fontSize: 28, color: '#d97706', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>System Certificate Ready</div>
                  <div style={{ fontSize: 12, color: '#888' }}>Issued {formatDate(entry.certificate.issuedAt)}</div>
                </div>
              </div>
              <button onClick={() => downloadSystemCert(entry.profile)} disabled={!!downloading} style={styles.certDlBtn}>
                {downloading ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaDownload style={{ marginRight: 6 }} />}
                Download PDF
              </button>
            </div>
          ) : entry?.uploadedCertificate?.fileName ? (
            <div style={styles.certRow}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                <FaUpload style={{ fontSize: 24, color: '#2563eb', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>Certificate Uploaded</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{entry.uploadedCertificate.fileName} · {formatDate(entry.uploadedCertificate.uploadedAt)}</div>
                </div>
              </div>
              <button onClick={() => downloadUploadedCert('current')} disabled={certDownloading === 'current'} style={styles.certDlBtn}>
                {certDownloading === 'current' ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaDownload style={{ marginRight: 6 }} />}
                Download
              </button>
            </div>
          ) : noInchargeAndCompleted ? (
            <div>
              <div style={{ padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <FaExclamationTriangle style={{ color: '#ea580c', flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <strong style={{ display: 'block', color: '#9a3412', fontSize: 13, marginBottom: 2 }}>Upload Certificate Required</strong>
                    <p style={{ margin: 0, fontSize: 12, color: '#7c2d12' }}>No DRP Incharge was assigned. Upload your certificate manually to complete this DRP.</p>
                  </div>
                </div>
              </div>
              <CertUploadWidget inputId="cert-current" uploading={certUploading === 'current'} onUpload={(payload) => uploadCertificate('current', payload)} />
            </div>
          ) : currentCompleted && !hasIncharge ? (
            <div>
              <p style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>No incharge assigned. Upload your certificate below.</p>
              <CertUploadWidget inputId="cert-current" uploading={certUploading === 'current'} onUpload={(payload) => uploadCertificate('current', payload)} />
            </div>
          ) : (
            <p style={{ fontSize: 13, color: '#888' }}>
              {currentCompleted
                ? 'Waiting for your DRP Incharge to issue the certificate.'
                : 'Certificate will be available after your DRP Incharge verifies all sections and issues it.'}
            </p>
          )}
        </div>

        {/* ── Past DRP History ── */}
        {drpHistory.length > 0 && (
          <>
            <div style={{ borderTop: '2px dashed #e5e7eb', margin: '4px 0 16px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <FaHistory style={{ color: '#6366f1', fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>Past DRPs</span>
              <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>— tap a card to expand details</span>
            </div>
            {[...drpHistory].reverse().map((hist, idx) => (
              <PastDRPCard
                key={hist._id}
                hist={hist}
                index={drpHistory.length - idx}
                onUploadCert={(histId, payload) => uploadCertificate(histId, payload)}
                onDownloadUploadedCert={(histId) => downloadUploadedCert(histId)}
                onDownloadSystemCert={(histProfile) => downloadSystemCert(histProfile.profile)}
                uploading={certUploading}
                downloading={certDownloading}
                onDelete={deleteDRP}
                deleting={deletingDRP}
              />
            ))}
          </>
        )}
      </SectionCard>

      {/* Modals */}
      {activeModal?.type === 'attendance' && (
        <FormModal title={activeModal.id ? 'Edit Attendance' : 'Add Attendance'} fields={attendanceFields} initialValues={activeModal.data || {}} onClose={() => setActiveModal(null)}
          onSave={vals => activeModal.id ? handleEdit('attendance', activeModal.id, vals) : handleAdd('attendance', vals)} />
      )}
      {activeModal?.type === 'diary' && (
        <FormModal title={activeModal.id ? 'Edit Diary Entry' : 'Add Diary Entry'} fields={diaryFields} initialValues={activeModal.data || {}} onClose={() => setActiveModal(null)}
          onSave={(vals) => {
            const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
            const withDay = { ...vals, day: vals.date ? days[new Date(vals.date).getDay()] : '' };
            return activeModal.id ? handleEdit('diary', activeModal.id, withDay) : handleAdd('diary', withDay);
          }} />
      )}
      {activeModal?.type === 'operation' && (
        <FormModal title={activeModal.id ? 'Edit Operation Record' : 'Add Operation Record'} fields={operationFields} initialValues={activeModal.data || {}} onClose={() => setActiveModal(null)}
          onSave={vals => activeModal.id ? handleEdit('operation', activeModal.id, vals) : handleAdd('operation', vals)} />
      )}
      {activeModal?.type === 'publicHealth' && (
        <FormModal title={activeModal.id ? 'Edit Activity' : 'Add Public Health Activity'} fields={publicHealthFields} initialValues={activeModal.data || {}} onClose={() => setActiveModal(null)}
          onSave={vals => activeModal.id ? handleEdit('publicHealth', activeModal.id, vals) : handleAdd('publicHealth', vals)} />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  container: { maxWidth: 680, margin: '0 auto', padding: '0 0 32px', paddingTop: 'env(safe-area-inset-top, 0px)', fontFamily: 'sans-serif', background: '#f5f5f7', minHeight: '100vh' },
  centered: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', position: 'sticky', top: 'env(safe-area-inset-top, 0px)', zIndex: 10 },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#b11217', padding: 4 },
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' },
  headerSub: { fontSize: 12, color: '#b91c1c', background: '#fee2e2', padding: '2px 8px', borderRadius: 12 },
  infoBanner: { display: 'flex', gap: 12, margin: 12, padding: 14, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12 },
  errorBanner: { margin: '0 12px 12px', padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 8, fontSize: 14 },
  completedBanner: { display: 'flex', alignItems: 'center', gap: 10, margin: '0 12px 12px', padding: '12px 14px', background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 12, flexWrap: 'wrap' },
  sectionCard: { margin: '0 12px 12px', background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  entryCard: { padding: '10px 12px', background: '#f9fafb', borderRadius: 8, marginBottom: 8, border: '1px solid #e5e7eb' },
  entryRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  entryDate: { fontSize: 12, color: '#555', display: 'flex', alignItems: 'center' },
  entryTag: { fontSize: 12, background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 10, fontWeight: 500 },
  emptyText: { color: '#999', fontSize: 13, fontStyle: 'italic', margin: '4px 0' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  primaryBtn: { background: '#b11217', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', fontSize: 14 },
  secondaryBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  addBtn: { background: '#b11217', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center' },
  outlineBtn: { background: 'none', color: '#b11217', border: '1px solid #b11217', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center' },
  linkBtn: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, padding: 0, display: 'flex', alignItems: 'center', marginTop: 4 },
  editBtn: { background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 },
  deleteBtn: { background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4 },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: 4 },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  detailItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  detailLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  selectedIncharge: { display: 'flex', alignItems: 'center', padding: '10px 12px', background: '#f0fdf4', border: '1px solid #a7f3d0', borderRadius: 8, marginBottom: 8 },
  searchDropdown: { border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', marginTop: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto' },
  searchItem: { padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', borderBottom: '1px solid #f3f4f6' },
  createInchargeBox: { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 14, marginTop: 12 },
  verifyBadge: { display: 'flex', alignItems: 'center', padding: '8px 12px', borderRadius: 8, fontSize: 13 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #e5e7eb' },
  toast: { position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' },
  pastDRPCard: { background: '#fafafa', borderRadius: 10, padding: 14, marginBottom: 10 },
  drpIndexBadge: { fontSize: 11, padding: '3px 8px', borderRadius: 8, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 },
  badgeSystemCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#d97706', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeUploadedCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  badgeNoCert: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#fee2e2', color: '#dc2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center' },
  certActionBtn: { fontSize: 12, padding: '6px 12px', borderRadius: 8, background: '#fff7ed', color: '#b45309', border: '1px solid #fed7aa', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', fontWeight: 600 },
  certRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, flexWrap: 'wrap' },
  certDlBtn: { background: '#b11217', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', flexShrink: 0 }
};

export default ClassicDRPModule;
